/**
 * Full-page ingestion gate — shown while a project is ingesting repositories.
 * Driven by real project + workflow signals (not simulated %).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIngestionSummary, useProject, useProjectWorkflow } from '../../../services/api';
import { useProjectLifecycle } from './useProjectLifecycle';
import { phase0TabPath } from './projectPhases';
import {
  activeIngestionPhase,
  computeIngestionProgressPercent,
  ingestionPhaseDetail,
  ingestionPhasePercent,
  ingestionPhaseQuietFor,
  ingestionPhaseStep,
  isIngestionCompleted,
  isIngestionFailed,
  isIngestionGateComplete,
  isIngestionRunning,
} from '../../../lib/ingestionGate';
import { clearBootstrapGateLock } from '../../../lib/bootstrapGateLock';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BookMarked,
  Check,
  Clock,
  Code2,
  File,
  GitBranch,
} from 'lucide-react';
import './ingestion-aurora.css';
import './ingestion-progress-aurora.css';

export interface IngestionProgressScreenProps {
  projectId: string;
  projectName?: string;
  /** Called after the 100% completion flash — layout can release the gate. */
  onComplete?: () => void;
}

const STEPS = [
  { id: 'clone', label: 'Connect & clone sources' },
  { id: 'scan', label: 'Scan files & languages' },
  { id: 'index', label: 'Index inventory' },
  { id: 'ready', label: 'Open Discover workspace' },
] as const;

function formatDuration(ms: number): string {
  const sec = Math.max(0, Math.round(ms / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ${String(sec % 60).padStart(2, '0')}s`;
  return `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m`;
}

const compactNumber = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });

export const IngestionProgressScreen: React.FC<IngestionProgressScreenProps> = ({
  projectId,
  projectName,
  onComplete,
}) => {
  const navigate = useNavigate();
  const { project } = useProject(projectId);
  const { data: workflow } = useProjectWorkflow(projectId);
  const { data: summary } = useIngestionSummary(projectId, { fastPoll: true });
  const lifecycle = useProjectLifecycle(projectId);

  const [elapsedSec, setElapsedSec] = useState(0);
  const [completedFlash, setCompletedFlash] = useState(false);

  useEffect(() => {
    const t = window.setInterval(() => setElapsedSec((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, []);

  const failed = isIngestionFailed(project);
  const running = isIngestionRunning(project?.status, project?.ingestionStatus, workflow);
  const ingestionComplete = isIngestionCompleted(project, workflow);
  const hasFiles = lifecycle.hasIngestedFiles;
  const fileCount = summary?.file_count ?? 0;
  // During a fresh ingest, ignore stale workflow readiness from the prior upload.
  const filesIndexedThisRun =
    running || project?.status === 'ingesting'
      ? fileCount > 0
      : hasFiles || fileCount > 0;

  const done = !failed && isIngestionGateComplete(project, workflow);

  const topLanguages = Object.entries(summary?.by_language ?? {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([lang]) => lang);

  // The phase the backend reported, with real counts. Without one (an older
  // backend) the ring falls back to the elapsed-time estimate.
  const phase = activeIngestionPhase(project?.ingestionPhase, running && !failed);

  const estimate = useMemo(
    () =>
      computeIngestionProgressPercent({
        failed,
        complete: done || completedFlash || ingestionComplete,
        running,
        elapsedSec,
        fileCount,
        hasIndexedFiles: filesIndexedThisRun,
      }),
    [failed, done, completedFlash, ingestionComplete, running, elapsedSec, fileCount, filesIndexedThisRun],
  );
  const progress =
    phase && !(done || completedFlash || ingestionComplete) ? ingestionPhasePercent(phase) : estimate;

  const stepIndex = failed
    ? 0
    : done || completedFlash
      ? 3
      : phase
        ? ingestionPhaseStep(phase)
        : filesIndexedThisRun
        ? 2
        : progress >= 28
          ? 1
          : 0;

  const quietMs = phase ? ingestionPhaseQuietFor(phase, Date.now()) : undefined;
  const phaseLine = phase
    ? ingestionPhaseDetail(phase) + (quietMs ? ` · no update for ${formatDuration(quietMs)}` : '')
    : undefined;
  const phaseStep = phase ? STEPS[ingestionPhaseStep(phase)].id : undefined;

  const stepDetail: Partial<Record<(typeof STEPS)[number]['id'], string>> = {
    clone: phaseStep === 'clone' ? phaseLine : undefined,
    scan:
      phaseStep === 'scan'
        ? phaseLine
        : topLanguages.length > 0
          ? `Detected ${topLanguages.join(', ')}`
          : undefined,
    index: phaseStep === 'index' ? phaseLine :
      fileCount > 0
        ? [
            `${fileCount.toLocaleString()} files`,
            summary?.total_loc ? `${compactNumber.format(summary.total_loc)} lines` : null,
            summary?.repository_count
              ? `${summary.repository_count} ${summary.repository_count === 1 ? 'repository' : 'repositories'}`
              : null,
          ]
            .filter(Boolean)
            .join(' · ')
        : undefined,
  };

  useEffect(() => {
    if (!done) return;
    setCompletedFlash(true);
    const t = window.setTimeout(() => {
      clearBootstrapGateLock(projectId);
      onComplete?.();
      navigate(`/projects/${projectId}`, { replace: true });
    }, 900);
    return () => window.clearTimeout(t);
  }, [done, navigate, onComplete, projectId]);

  const headline = failed
    ? 'Ingestion needs attention'
    : done || completedFlash
      ? 'Repository ready'
      : 'Preparing your workspace';

  const subtitle = failed
    ? project?.ingestionError || 'Something went wrong while reading the repository.'
    : done || completedFlash
      ? 'Taking you into Discover…'
      : hasFiles || fileCount > 0
        ? 'Files are appearing. Finishing scan and index before Discover opens.'
        : 'We’re cloning and indexing your sources. Discover opens when this finishes.';

  const isComplete = done || completedFlash;
  const ringState = failed ? 'Needs attention' : isComplete ? 'Ready' : running ? 'In progress' : 'Starting';
  const pct = failed ? 100 : progress;
  const RING_C = 2 * Math.PI * 52;

  const stats = [
    { label: 'Elapsed', icon: Clock, value: formatDuration(elapsedSec * 1000) },
    { label: 'Files indexed', icon: File, value: fileCount > 0 ? fileCount.toLocaleString() : '—' },
    { label: 'Lines of code', icon: Code2, value: summary?.total_loc ? compactNumber.format(summary.total_loc) : '—' },
    { label: 'Repositories', icon: GitBranch, value: summary?.repository_count ? String(summary.repository_count) : '—' },
  ];

  return (
    <div className="ingestion-aurora-page">
      <section className={`pw${failed ? ' failed' : ''}`} aria-labelledby="pw-title" aria-live="polite">
        <div className="pw-brand">
          <span className="pw-tile" aria-hidden="true">
            {failed ? <AlertTriangle size={22} /> : <BookMarked size={22} />}
          </span>
          <div>
            <div className="pw-eyebrow">{failed ? 'Ingestion failed' : 'Repository ingestion'}</div>
            <h1 id="pw-title" className="pw-title">
              {headline}
            </h1>
            <p className="pw-sub">
              {projectName ? (
                <>
                  <b>{projectName}</b>
                  {' · '}
                </>
              ) : null}
              {subtitle}
            </p>
          </div>
        </div>

        <div className="pw-overview">
          <div className="pw-ring-box">
            <div
              className="pw-ring"
              role="progressbar"
              aria-label="Ingestion progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
            >
              <svg viewBox="0 0 120 120" aria-hidden="true">
                <defs>
                  <linearGradient id="pw-flow" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="var(--pw-flow-a)" />
                    <stop offset="1" stopColor="var(--pw-flow-b)" />
                  </linearGradient>
                </defs>
                <circle className="track" cx="60" cy="60" r="52" fill="none" strokeWidth="10" />
                <circle
                  className="fill"
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke="url(#pw-flow)"
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={RING_C}
                  strokeDashoffset={RING_C * (1 - pct / 100)}
                />
              </svg>
              <div className="center">
                <div className="pct">{failed ? '!' : `${progress}%`}</div>
                <div className="lbl">{ringState}</div>
              </div>
            </div>
          </div>
          <div className="pw-stats">
            {stats.map(({ label, icon: Icon, value }) => (
              <div key={label} className="pw-stat">
                <span className="ic" aria-hidden="true">
                  <Icon size={14} />
                </span>
                <div>
                  <div className="k">{label}</div>
                  <div className="v">{value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <ol className="pw-steps" aria-label="Ingestion steps">
          {STEPS.map((step, i) => {
            const active = i === stepIndex && !failed && !isComplete;
            const complete = i < stepIndex || isComplete;
            const detail = complete || active ? stepDetail[step.id] : undefined;
            const state = complete ? 'done' : active ? 'active' : 'waiting';
            return (
              <li key={step.id} className={`pw-step ${state}`} aria-current={active ? 'step' : undefined}>
                <span className="pw-node" aria-hidden="true">
                  {complete ? <Check size={12} strokeWidth={2.6} /> : null}
                </span>
                <div>
                  <div className="s-title">{step.label}</div>
                  <div className="s-sub">{detail ?? (complete ? 'Done' : active ? 'In progress' : 'Waiting')}</div>
                </div>
              </li>
            );
          })}
        </ol>

        <div className="pw-actions">
          <button type="button" className="pw-btn ghost" onClick={() => navigate('/projects/all')}>
            <ArrowLeft size={17} aria-hidden="true" />
            Back to projects
          </button>
          <button
            type="button"
            className="pw-btn primary"
            disabled={!failed && !isComplete}
            onClick={() => navigate(phase0TabPath(projectId, 'repository'))}
          >
            {failed ? 'Open Discover anyway' : 'Open Discover'}
            <ArrowRight size={17} aria-hidden="true" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default IngestionProgressScreen;
